import React, { createContext, useState, useMemo, useContext } from "react"
import { styled, Theme, ThemeProvider } from "@mui/material/styles"
import { Box, CssBaseline } from "@mui/material"
import {
  christmasTheme,
  defaultTheme,
  glassTheme,
  lightTheme,
  ultraDarkTheme,
  classicTheme,
  halloweenTheme,
} from "./theme"
import { LocalTheme, LocalDefaultTheme, LocalForceShow } from "../utils/localStorage"

interface ThemeContextType {
  toggleTheme: () => void
  setTheme: (mode: string) => void
  themesShown: Theme[]
  mode: string
  flash: () => void
  setDarken: (darken: boolean) => void
}

const now = new Date()
const currentDate = now.getDate()
const currentMonth = now.getMonth() + 1

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

let allThemes = [defaultTheme, lightTheme, ultraDarkTheme, glassTheme, classicTheme, christmasTheme, halloweenTheme]
let eventTheme =
  currentMonth === 12
    ? christmasTheme
    : currentMonth === 2 && currentDate >= 24
      ? classicTheme
      : currentMonth === 10 && currentDate >= 20
        ? halloweenTheme
        : null
let availableThemes = [defaultTheme, lightTheme, ultraDarkTheme, glassTheme]
let themesShown = [defaultTheme, lightTheme, ultraDarkTheme]
if (eventTheme) {
  availableThemes.push(eventTheme)
  themesShown.unshift(eventTheme)
}
const forceShowThemes = (LocalForceShow.get() || "").split(",").filter((t: string) => t !== "")
forceShowThemes.forEach((themeName) => {
  const theme = allThemes.find((t) => t.name === themeName)
  if (theme && !availableThemes.includes(theme)) availableThemes.push(theme)
  if (theme && !themesShown.includes(theme)) themesShown.push(theme)
})
const fallbackTheme = themesShown[0].name!

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  let storedTheme = LocalTheme.get() || fallbackTheme
  if (!(storedTheme && availableThemes.map((t) => t.name).includes(storedTheme))) {
    LocalTheme.set(fallbackTheme)
    storedTheme = fallbackTheme
  }
  let lastDefaultTheme = LocalDefaultTheme.get()
  if (fallbackTheme != lastDefaultTheme) {
    LocalDefaultTheme.set(fallbackTheme)
    if (eventTheme?.name === fallbackTheme || storedTheme === lastDefaultTheme) {
      LocalTheme.set(fallbackTheme)
      storedTheme = fallbackTheme
    }
  }

  const [mode, setMode] = useState(storedTheme)
  const [displayFlash, setDisplayFlash] = useState(false)
  const [darken, setDarken] = useState(false)

  const toggleTheme = () => {
    let newMode = fallbackTheme
    let i = themesShown.map((t) => t.name).indexOf(storedTheme)
    if (i != -1) newMode = themesShown[(i + 1) % themesShown.length].name!
    LocalTheme.set(String(newMode))
    setMode(newMode)
  }

  const setTheme = (newMode: string) => {
    LocalTheme.set(String(newMode))
    setMode(newMode)
  }

  const flash = () => {
    setDisplayFlash(true)
    setTimeout(() => {
      setDisplayFlash(false)
    }, 800)
  }

  const theme = useMemo(() => availableThemes.find((t) => t.name === mode) || defaultTheme, [mode])

  return (
    <ThemeContext.Provider value={{ toggleTheme, setTheme, themesShown, mode, flash, setDarken }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <FlashBox
          sx={{
            display: displayFlash ? "block" : "none",
          }}
        />
        <DarkBox
          sx={{
            opacity: darken ? 0.9 : 0,
          }}
        />
        <Box sx={{ backgroundColor: theme.palette.element.main.background, minHeight: "100vh", width: "100%" }}>
          {children}
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}

export const useThemeContext = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProviderWrapper")
  }
  return context
}

const FlashBox = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 5000,
  backgroundColor: "white",
  pointerEvents: "none",
  userSelect: "none",
  animation: `flash 0.8s ease-out forwards`,
  "@keyframes flash": {
    "0%": {
      backdropFilter: "blur(5px)",
      backgroundColor: "white",
    },
    "80%": {
      backdropFilter: "blur(0px)",
    },
    "100%": {
      backgroundColor: "transparent",
    },
  },
})

const DarkBox = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 5000,
  backgroundColor: "#00000040",
  pointerEvents: "none",
  userSelect: "none",
  backdropFilter: "saturate(0%)",
  transition: "opacity 0.5s ease-in-out",
})
